"use client";

import { useEffect, useSyncExternalStore } from "react";

type Theme = "light" | "dark" | "system";
let fallbackTheme: Theme = "system";
function readTheme(): Theme {
  try {
    const value = localStorage.getItem("theme");
    return value === "light" || value === "dark" ? value : "system";
  } catch { return fallbackTheme; }
}
function subscribe(callback: () => void) {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  window.addEventListener("storage", callback);
  window.addEventListener("theme-changed", callback);
  media.addEventListener("change", callback);
  return () => {
    window.removeEventListener("storage", callback);
    window.removeEventListener("theme-changed", callback);
    media.removeEventListener("change", callback);
  };
}
function getSnapshot() {
  return `${readTheme()}:${window.matchMedia("(prefers-color-scheme: dark)").matches}`;
}

export function ThemeToggle() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => "pending");
  const mounted = snapshot !== "pending";
  const theme = (mounted ? snapshot.split(":")[0] : "system") as Theme;
  const isDark = theme === "dark" || (theme === "system" && snapshot.endsWith(":true"));

  useEffect(() => {
    if (mounted) document.documentElement.classList.toggle("dark", isDark);
  }, [mounted, isDark]);

  if (!mounted) return <div className="w-11 h-11" />;

  function cycle() {
    fallbackTheme = theme === "system" ? "dark" : theme === "dark" ? "light" : "system";
    try {
      if (fallbackTheme === "system") localStorage.removeItem("theme");
      else localStorage.setItem("theme", fallbackTheme);
    } catch { /* Keep the preference for this session when storage is disabled. */ }
    window.dispatchEvent(new Event("theme-changed"));
  }

  return (
    <button
      onClick={cycle}
      className="flex items-center justify-center w-11 h-11 rounded-sm transition-colors hover:bg-forest/[0.06] dark:hover:bg-white/[0.08] bg-transparent border-none cursor-pointer"
      aria-label={`Theme: ${theme}. Click to change.`}
      title={`Theme: ${theme}`}
    >
      {isDark ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-text-body">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-text-body">
          <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      )}
    </button>
  );
}
